import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { Share2, Clock, File, User, Eye, Edit, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';
import { Tabs, TabsList, TabsContent, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface SharedRecord {
  id: string;
  documentTitle: string;
  recipientEmail: string;
  permission: 'view' | 'edit';
  sharedAt: string;
  message?: string;
  document_id?: string;
  owner_id?: string;
  sharedBy?: string;
}

const SharedDocuments: React.FC = () => {
  const [sharedByMe, setSharedByMe] = useState<SharedRecord[]>([]);
  const [sharedWithMe, setSharedWithMe] = useState<SharedRecord[]>([]);
  const { language } = useLanguage();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Load shared documents
    const loadSharedDocuments = async () => {
      if (!user) {
        // If not logged in, load from localStorage
        try {
          const sharedDocs = JSON.parse(localStorage.getItem('adaaltBotSharedDocs') || '[]');
          setSharedByMe(sharedDocs);
          
          // For demonstration, we'll create mock data for documents shared with me
          const mockSharedWithMe = [
            {
              id: '1',
              documentTitle: 'Property Sale Agreement',
              recipientEmail: 'me@example.com',
              permission: 'view' as const,
              sharedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
              message: 'Please review this agreement',
              sharedBy: 'ahmed@lawfirm.com'
            },
            {
              id: '2',
              documentTitle: 'Divorce Petition',
              recipientEmail: 'me@example.com',
              permission: 'edit' as const,
              sharedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago
              sharedBy: 'sara@legalaid.org'
            }
          ];
          
          setSharedWithMe(mockSharedWithMe as SharedRecord[]);
        } catch (error) {
          console.error("Error loading shared documents:", error);
          toast({
            variant: "destructive",
            title: "Error",
            description: language === 'ur' ? 'دستاویزات لوڈ کرنے میں خرابی' : 'Error loading documents'
          });
        }
        return;
      }
      
      setIsLoading(true);
      
      try {
        // Load documents shared by me from Supabase
        const { data: sharedByMeData, error: sharedByMeError } = await supabase
          .from('shared_documents')
          .select(`
            id,
            permission,
            recipient_email,
            shared_at,
            message,
            owner_id,
            document_id,
            saved_documents(title)
          `)
          .eq('owner_id', user.id);
          
        if (sharedByMeError) throw sharedByMeError;
        
        if (sharedByMeData) {
          const formattedSharedByMe = sharedByMeData.map(record => ({
            id: record.id,
            documentTitle: record.saved_documents?.title || 'Unnamed Document',
            recipientEmail: record.recipient_email,
            permission: record.permission as 'view' | 'edit',
            sharedAt: record.shared_at,
            message: record.message,
            document_id: record.document_id,
            owner_id: record.owner_id
          }));
          
          setSharedByMe(formattedSharedByMe);
          
          // Also save to localStorage for offline access
          localStorage.setItem('adaaltBotSharedDocs', JSON.stringify(formattedSharedByMe));
        }
        
        // TODO: In a real app with complete auth, we would query for documents shared with the current user
        // For now, we'll keep using the mock data
        
      } catch (error) {
        console.error("Error loading shared documents from Supabase:", error);
        
        // Fallback to localStorage
        const sharedDocs = JSON.parse(localStorage.getItem('adaaltBotSharedDocs') || '[]');
        setSharedByMe(sharedDocs);
      } finally {
        setIsLoading(false);
      }
    };

    loadSharedDocuments();
    
    // Add event listener for storage changes (if user has multiple tabs open)
    window.addEventListener('storage', loadSharedDocuments);
    
    return () => {
      window.removeEventListener('storage', loadSharedDocuments);
    };
  }, [language, user]);

  const revokeAccess = async (id: string) => {
    try {
      if (user && navigator.onLine) {
        // Delete from Supabase
        const { error } = await supabase
          .from('shared_documents')
          .delete()
          .eq('id', id);
          
        if (error) throw error;
      }
      
      // Filter out the document with the given id
      const updatedSharedDocs = sharedByMe.filter(doc => doc.id !== id);
      setSharedByMe(updatedSharedDocs);
      
      // Save updated list to localStorage
      localStorage.setItem('adaaltBotSharedDocs', JSON.stringify(updatedSharedDocs));
      
      toast({
        title: "Success", 
        description: language === 'ur' ? 'رسائی کامیابی سے منسوخ کر دی گئی' : 'Access revoked successfully'
      });
    } catch (error) {
      console.error("Error revoking access:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: language === 'ur' ? 'رسائی منسوخ کرنے میں خرابی' : 'Error revoking access'
      });
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(language === 'ur' ? 'ur-PK' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const openDocument = (id: string) => {
    // In a real app, this would open the document
    toast({
      title: "Info", 
      description: language === 'ur' ? 'دستاویز کھول رہا ہے...' : 'Opening document...'
    });
  };

  if (isLoading) {
    return (
      <Card className="mt-4">
        <CardContent className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`mt-4 ${language === 'ur' ? 'rtl' : 'ltr'}`}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-xl flex items-center gap-2">
          <Share2 className="h-5 w-5" />
          {language === 'ur' ? 'شیئر شدہ دستاویزات' : 'Shared Documents'}
        </CardTitle>
      </CardHeader>
      
      <CardContent>
        <Tabs defaultValue="shared-by-me" className={`${language === 'ur' ? 'rtl' : 'ltr'}`}>
          <TabsList className="mb-4">
            <TabsTrigger value="shared-by-me">
              {language === 'ur' ? 'میرے ذریعے شیئر کی گئی' : 'Shared By Me'}
            </TabsTrigger>
            <TabsTrigger value="shared-with-me">
              {language === 'ur' ? 'میرے ساتھ شیئر کی گئی' : 'Shared With Me'}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="shared-by-me">
            {sharedByMe.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  {language === 'ur' ? 'آپ نے ابھی تک کوئی دستاویز شیئر نہیں کی ہے۔' : "You haven't shared any documents yet."}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {sharedByMe.map((doc) => (
                  <div
                    key={doc.id}
                    className="p-4 border rounded-lg hover:border-primary transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="space-y-1" onClick={() => openDocument(doc.id)}>
                        <div className="flex items-center">
                          <File className={`h-4 w-4 ${language === 'ur' ? 'ml-2' : 'mr-2'} text-primary`} />
                          <h3 className="font-medium">{doc.documentTitle}</h3>
                        </div>
                        
                        <div className="flex items-center text-sm text-gray-500">
                          <User className={`h-4 w-4 ${language === 'ur' ? 'ml-1' : 'mr-1'}`} />
                          {language === 'ur' ? 'شیئر کی گئی بذریعہ' : 'Shared with'}: {doc.recipientEmail}
                        </div>
                        
                        <div className="flex items-center gap-3 mt-2">
                          <div className="flex items-center text-sm text-gray-500">
                            <Clock className={`h-4 w-4 ${language === 'ur' ? 'ml-1' : 'mr-1'}`} />
                            {formatDate(doc.sharedAt)}
                          </div>
                          
                          <Badge variant={doc.permission === 'edit' ? 'outline' : 'secondary'} className="text-xs">
                            {doc.permission === 'edit' ? (
                              <Edit className={`h-3 w-3 ${language === 'ur' ? 'ml-1' : 'mr-1'}`} />
                            ) : (
                              <Eye className={`h-3 w-3 ${language === 'ur' ? 'ml-1' : 'mr-1'}`} />
                            )}
                            {doc.permission === 'edit' 
                              ? (language === 'ur' ? 'ترمیم کر سکتے ہیں' : 'Can edit')
                              : (language === 'ur' ? 'صرف دیکھ سکتے ہیں' : 'View only')}
                          </Badge>
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => revokeAccess(doc.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        {language === 'ur' ? 'منسوخ کریں' : 'Revoke'}
                      </Button>
                    </div>
                    
                    {doc.message && (
                      <div className="mt-3 p-3 bg-muted rounded-md text-sm">
                        <p className="font-medium mb-1">{language === 'ur' ? 'پیغام' : 'Message'}:</p>
                        <p>{doc.message}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="shared-with-me">
            {sharedWithMe.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  {language === 'ur' ? 'ابھی تک آپ کے ساتھ کوئی دستاویز شیئر نہیں کی گئی ہے۔' : 'No documents have been shared with you yet.'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {sharedWithMe.map((doc) => (
                  <div
                    key={doc.id}
                    className="p-4 border rounded-lg hover:border-primary transition-colors cursor-pointer"
                    onClick={() => openDocument(doc.id)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <div className="flex items-center">
                          <File className={`h-4 w-4 ${language === 'ur' ? 'ml-2' : 'mr-2'} text-primary`} />
                          <h3 className="font-medium">{doc.documentTitle}</h3>
                        </div>
                        
                        <div className="flex items-center text-sm text-gray-500">
                          <User className={`h-4 w-4 ${language === 'ur' ? 'ml-1' : 'mr-1'}`} />
                          {language === 'ur' ? 'شیئر کی گئی بذریعہ' : 'Shared by'}: {(doc as any).sharedBy}
                        </div>
                        
                        <div className="flex items-center gap-3 mt-2">
                          <div className="flex items-center text-sm text-gray-500">
                            <Clock className={`h-4 w-4 ${language === 'ur' ? 'ml-1' : 'mr-1'}`} />
                            {formatDate(doc.sharedAt)}
                          </div>
                          
                          <Badge variant={doc.permission === 'edit' ? 'outline' : 'secondary'} className="text-xs">
                            {doc.permission === 'edit' ? (
                              <Edit className={`h-3 w-3 ${language === 'ur' ? 'ml-1' : 'mr-1'}`} />
                            ) : (
                              <Eye className={`h-3 w-3 ${language === 'ur' ? 'ml-1' : 'mr-1'}`} />
                            )}
                            {doc.permission === 'edit' 
                              ? (language === 'ur' ? 'ترمیم کر سکتے ہیں' : 'Can edit')
                              : (language === 'ur' ? 'صرف دیکھ سکتے ہیں' : 'View only')}
                          </Badge>
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex items-center gap-1"
                      >
                        <ExternalLink className="h-4 w-4" />
                        {language === 'ur' ? 'کھولیں' : 'Open'}
                      </Button>
                    </div>
                    
                    {doc.message && (
                      <div className="mt-3 p-3 bg-muted rounded-md text-sm">
                        <p className="font-medium mb-1">{language === 'ur' ? 'پیغام' : 'Message'}:</p>
                        <p>{doc.message}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default SharedDocuments;
